import { Link } from 'react-router-dom';
import {
  DEFAULT_GROWTH_PHASES,
  isMixUnallocated,
  mergeProfileGrowth,
} from '../model/growthProjection';
import { GROWTH_PRESETS } from '../model/seedDefaults';

export default function GrowthPanel({ growth, profiles, onChange, onApplyPreset }) {
  const profileGrowth = mergeProfileGrowth(growth.profileGrowth || [], profiles);
  const phases =
    growth.phases?.length > 0 ? growth.phases : DEFAULT_GROWTH_PHASES.map((p) => ({ ...p }));

  const updateGrowth = (patch) => onChange({ ...growth, ...patch, preset: 'custom' });

  const updateProfileRow = (profileId, patch) => {
    updateGrowth({
      profileGrowth: profileGrowth.map((row) =>
        row.profileId === profileId ? { ...row, ...patch } : row
      ),
    });
  };

  const updatePhase = (index, patch) => {
    const next = phases.map((phase, i) => (i === index ? { ...phase, ...patch } : phase));
    updateGrowth({ phases: next });
  };

  const shareTotal = profileGrowth.reduce((s, r) => s + (r.share0 || 0), 0);

  return (
    <section className="chart-card economics-panel">
      <h2>Scale drivers</h2>
      <p className="chart-hint">
        Stress paying-linker headcount and profile mix for <strong>Plaid COGS</strong> — not company
        revenue forecast. For MRR planning, use{' '}
        <Link to="/forecast">Forecast</Link>.
      </p>

      <div className="economics-preset-row">
        {Object.entries(GROWTH_PRESETS).map(([key, preset]) => (
          <button
            key={key}
            type="button"
            className={growth.preset === key ? 'primary-button' : 'secondary-button'}
            onClick={() => onApplyPreset(key)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="economics-form-grid economics-form-grid-3">
        <label className="economics-field">
          <span>Growth basis</span>
          <select
            value={growth.growthMode || 'profile'}
            onChange={(e) => updateGrowth({ growthMode: e.target.value })}
          >
            <option value="profile">Per-profile compounding</option>
            <option value="phased">Global phases + mix drift</option>
          </select>
        </label>
        <label className="economics-field">
          <span>Projection model</span>
          <select
            value={growth.model || 'monte_carlo'}
            onChange={(e) => updateGrowth({ model: e.target.value })}
          >
            <option value="monte_carlo">Monte Carlo (P10/P50/P90)</option>
            <option value="deterministic">Deterministic (fixed rate)</option>
          </select>
        </label>
        <label className="economics-field">
          <span>Growth distribution</span>
          <select
            value={growth.distribution || 'lognormal'}
            onChange={(e) => updateGrowth({ distribution: e.target.value })}
            disabled={growth.model === 'deterministic'}
          >
            <option value="lognormal">Lognormal (default — early-stage skew)</option>
            <option value="normal">Normal</option>
            <option value="fixed">Fixed (no volatility)</option>
          </select>
        </label>
        <label className="economics-field">
          <span>MC runs</span>
          <input
            type="number"
            min="50"
            max="5000"
            step="50"
            value={growth.mcRuns || 500}
            disabled={growth.model !== 'monte_carlo'}
            onChange={(e) =>
              updateGrowth({
                mcRuns: Math.min(5000, Math.max(50, parseInt(e.target.value, 10) || 500)),
              })
            }
          />
        </label>
      </div>

      <div className="economics-form-grid economics-form-grid-3">
        <label className="economics-field">
          <span>Starting paying users</span>
          <input
            type="number"
            min="0"
            value={growth.startPaying}
            onChange={(e) =>
              updateGrowth({ startPaying: Math.max(0, parseInt(e.target.value, 10) || 0) })
            }
          />
        </label>
        <label className="economics-field">
          <span>Horizon (months)</span>
          <input
            type="number"
            min="1"
            max="36"
            value={growth.horizonMonths}
            onChange={(e) =>
              updateGrowth({
                horizonMonths: Math.min(36, Math.max(1, parseInt(e.target.value, 10) || 12)),
              })
            }
          />
        </label>
        <label className="economics-field">
          <span>Monthly churn (%)</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={growth.churnPct}
            onChange={(e) =>
              updateGrowth({
                churnPct: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)),
              })
            }
          />
        </label>
      </div>

      <h3 className="economics-subheading">Growth phases</h3>
      <p className="chart-hint">
        Through month = last month in phase.{' '}
        {growth.growthMode === 'phased'
          ? 'Phase means compound total paying users; profile rows shift the mix.'
          : 'Profile rows compound each profile; phase volatility supplies the regime spread.'}
      </p>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Through month</th>
              <th>Mean growth %/mo</th>
              <th>Volatility %</th>
            </tr>
          </thead>
          <tbody>
            {phases.map((phase, index) => (
              <tr key={`${phase.label}-${index}`}>
                <td>
                  <input
                    className="economics-inline-input economics-inline-input-wide"
                    value={phase.label || ''}
                    onChange={(e) => updatePhase(index, { label: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    min="1"
                    max="36"
                    value={phase.throughMonth}
                    onChange={(e) =>
                      updatePhase(index, {
                        throughMonth: Math.min(
                          36,
                          Math.max(1, parseInt(e.target.value, 10) || 1)
                        ),
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    step="0.5"
                    value={phase.monthlyGrowthPct}
                    disabled={growth.growthMode !== 'phased'}
                    title={
                      growth.growthMode === 'phased'
                        ? 'Used by the global phased model'
                        : 'Choose Global phases + mix drift to edit phase means'
                    }
                    onChange={(e) =>
                      updatePhase(index, {
                        monthlyGrowthPct: Math.max(-95, parseFloat(e.target.value) || 0),
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    step="1"
                    value={phase.volatilityPct}
                      onChange={(e) =>
                        updatePhase(index, {
                          volatilityPct: Math.max(0, parseFloat(e.target.value) || 0),
                        })
                      }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="economics-subheading">Profile mix</h3>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Profile</th>
              <th>Start share</th>
              <th>{growth.growthMode === 'phased' ? 'Mix drift %/mo' : 'Growth %/mo'}</th>
            </tr>
          </thead>
          <tbody>
            {profileGrowth.map((row) => {
              const profile = profiles.find((p) => p.id === row.profileId);
              return (
                <tr key={row.profileId}>
                  <td>{profile?.name || row.profileId}</td>
                  <td>
                    <input
                      className="economics-inline-input"
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={row.share0}
                      onChange={(e) =>
                        updateProfileRow(row.profileId, {
                          share0: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)),
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="economics-inline-input"
                      type="number"
                      step="0.5"
                      value={row.monthlyGrowthPct}
                      onChange={(e) =>
                        updateProfileRow(row.profileId, {
                          monthlyGrowthPct: Math.max(-95, parseFloat(e.target.value) || 0),
                        })
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="chart-hint">
        Share sum: {(shareTotal * 100).toFixed(0)}% (normalized when sum > 0; zero stays unallocated — $0 P&L)
      </p>
      {isMixUnallocated(growth) && (
        <p className="module-status module-status-error economics-callout">
          Paying headcount has no profile mix — P&L is $0 until shares are set.
        </p>
      )}
    </section>
  );
}
